import { inject } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { tapResponse } from "@ngrx/operators";
import { switchMap, tap, pipe, withLatestFrom, takeUntil, distinctUntilChanged, map } from "rxjs";
import { PageResult } from "src/app/shared/models/pageResult";
import { toSignal } from "@angular/core/rxjs-interop";
import { DistributionGroup } from "src/app/models/distribution-group.model";
import { DistributionGroupApiService } from "src/app/services/distribution-group-api.service";
import { ManipulationsToolbarStore } from "src/app/store/manipulations-toolbar-store";
import { GlobalStore } from "src/app/store/global-store";
import { TostPositionEnum, TostTypeEnum } from "src/app/shared/models/tostes.model";
import { TostesStore } from "src/app/store/tostes-store";

export interface DistributionGroupState {
    distributionGroups: PageResult<DistributionGroup>;
    distributionGroupsExtra: PageResult<DistributionGroup>;
    readonly loading: boolean;
    readonly loadingExtra: boolean;
    isRequestOfExtaraWasEmpty: boolean;

}
export class DistributionGroupStore extends ComponentStore<DistributionGroupState> {
    #distributionGroupService = inject(DistributionGroupApiService)
    #manipulationsToolbarStore = inject(ManipulationsToolbarStore)
    #globalStore = inject(GlobalStore);
    #tostesStore = inject(TostesStore);

    readonly distributionGroup$ = this.select((x) => x.distributionGroups);
    readonly distributionGroupSig$ = toSignal(this.distributionGroup$);
    readonly distributionGroupExtra$ = this.select((x) => x.distributionGroupsExtra);
    readonly distributionGroupExtraSig$ = toSignal(this.distributionGroupExtra$);
    readonly totals$ = this.select((x) => x.distributionGroups.totals);
    readonly isEmpty$ = this.select((x) => x.distributionGroups.isEmpty);
    readonly loadingExtra$ = this.select((x) => x.loadingExtra);
    readonly loadingExtraSig$ = toSignal(this.loadingExtra$);
    readonly loading$ = this.select((x) => x.loading);
    readonly isRequestOfExtaraWasEmptySig$ = toSignal(this.select((x) => x.isRequestOfExtaraWasEmpty))


    constructor() {
        super({
            distributionGroups: { result: [], totals: 0 },
            distributionGroupsExtra: { result: [], totals: 0 },
            loading: false,
            loadingExtra: false,
            isRequestOfExtaraWasEmpty: false
        });

        this.#manipulationsToolbarStore.selectedFilter$
            .pipe(
                takeUntil(this.destroy$))
            .subscribe(x => {
                this.getDistributionGroup()
            });

        this.#manipulationsToolbarStore.isDisplayNotActiveItems$
            .pipe(
                takeUntil(this.destroy$))
            .subscribe(x => {
                this.getDistributionGroup()
            });

        this.getDistributionGroup();

        this.#globalStore.refreshGrops$.subscribe(ev => {
            this.getDistributionGroup(true)
        }
        )
    }

    readonly getDistributionGroup = this.effect<void | boolean>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            tap((ev) => {
                if (ev) {
                    this.#manipulationsToolbarStore.resetPage();
                }
            }),
            withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$, this.#manipulationsToolbarStore.isDisplayNotActiveItems$),
            switchMap(([event, selectedFilter, page, isDisplayNotActiveItems]) => this.#distributionGroupService.getAllDistributionGroups(event ? 1 : page, { ...selectedFilter, isActive: !isDisplayNotActiveItems })),
            tap((distributionGroups: PageResult<DistributionGroup>) => this.patchState({ distributionGroups })),
            tap(() => {
                this.patchState({ loading: false })
                this.loadDistributionGroupForExtra()
            }),
        )
    );

    readonly loadDistributionGroupForExtra = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loadingExtra: true })),
            tap(() => this.#manipulationsToolbarStore.updatePage()),
            withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$, this.#manipulationsToolbarStore.isDisplayNotActiveItems$),
            switchMap(([_, selectedFilter, page, isDisplayNotActiveItems]) => this.#distributionGroupService.getDistributionGroupsForExtra(page, { ...selectedFilter, isActive: !isDisplayNotActiveItems })
            ),
            tap((result: PageResult<DistributionGroup>) => {
                this.patchState({
                    distributionGroupsExtra: result,
                })
            }),
            tap(() => this.patchState({ loadingExtra: false })),
        )
    );

    readonly updateDistributionGroupsWithExtra = this.updater((state) => (
        {
            ...state,
            isRequestOfExtaraWasEmpty: ((this.distributionGroupExtraSig$()?.result == undefined || this.distributionGroupExtraSig$()?.result.length == 0) && this.loadingExtraSig$()) ? true : false,
            distributionGroups: {
                result: [...(this.distributionGroupSig$()?.result || []), ...(this.distributionGroupExtraSig$()?.result || [])],
                totals: this.distributionGroupSig$()?.totals || 0
            },
            distributionGroupsExtra: { result: [], totals: 0 }
        }
    ));

    readonly changeStatusDistributionGroup = this.effect<number>(
        pipe(
            map(id => {
                const guid = this.#tostesStore.generateGuid();
                this.#tostesStore.addTost({ guid, message: { en: "Changes distribution group status....", he: "משנה סטטוס קבוצת הפצה..." }, position: TostPositionEnum.BottomLeft, type: TostTypeEnum.LOAD });
                return { id, guid };
            }),
            switchMap((props) =>
                this.#distributionGroupService.changeStatusDistributionGroup(props.id).pipe(
                    tapResponse((sucsess) => {
                        this.#tostesStore.removeTostByGuid(props.guid)
                        this.#tostesStore.addTost({ guid: "", message: { en: `The status of the group has been successfully updated`, he: `סטטוס הקבוצה עודכן בהצלחה` }, type: TostTypeEnum.SUCCESS, seconds: 5 });
                        this.#manipulationsToolbarStore.resetPage();
                        this.getDistributionGroup()
                    },
                        (error) => {
                            this.#tostesStore.removeTostByGuid(props.guid)
                            this.#tostesStore.addTost({ guid: "", message: { en: "An error occurred while updating the distribution group status", he: "אירעה שגיאה בעת עדכון סטטוס קבוצת ההפצה" }, type: TostTypeEnum.ERROR, seconds: 5 });

                        }
                    )
                )
            )
        )
    );

    // readonly createDistributionGroup = this.effect<DistributionGroup>(
    //     pipe(
    //         switchMap((destributionGroup) =>
    //             this.#distributionGroupService.createDistributionGroup(destributionGroup).pipe(
    //                 tap((x) => {
    //                     this.getDistributionGroup()
    //                 })
    //             )
    //         )
    //     )
    // );

    readonly updateDistributionGroup = this.effect<DistributionGroup>(
        pipe(
            map(destributionGroup => {
                const guid = this.#tostesStore.generateGuid();
                this.#tostesStore.addTost({ guid, message: { en: "Updating distribution group information...", he: "מעדכן פרטי קבוצת הפצה..." }, position: TostPositionEnum.BottomLeft, type: TostTypeEnum.LOAD });
                return { destributionGroup, guid };
            }),
            switchMap((props) =>
                this.#distributionGroupService.updateDistributionGroup(props.destributionGroup).pipe(
                    tapResponse((sucsess) => {
                        this.#tostesStore.removeTostByGuid(props.guid)
                        this.#tostesStore.addTost({ guid: "", message: { en: `The ${props.destributionGroup.name} group has been successfully updated`, he: `הקבוצה ${props.destributionGroup.name} עודכנה בהצלחה ` }, type: TostTypeEnum.SUCCESS, seconds: 5 });
                        this.getDistributionGroup(true);
                    },
                        (error) => {
                            this.#tostesStore.removeTostByGuid(props.guid)
                            this.#tostesStore.addTost({ guid: "", message: { en: "An error occurred while updating the distribution group information", he: "אירעה שגיאה בעת עדכון פרטי קבוצת ההפצה" }, type: TostTypeEnum.ERROR, seconds: 5 });

                        }
                    )
                )
            )
        )
    );
}
